import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";

const DisclaimerGenerator = () => {
  const [formData, setFormData] = useState({
    websiteName: "",
    websiteURL: "",
    country: "",
    state: "",
    email: "",
    collectUserData: true,
    lastUpdated: new Date().toISOString().split("T")[0],
  });
  const [generatedDisclaimer, setGeneratedDisclaimer] = useState("");
  const [downloadDisclaimer, setDownloadDisclaimer] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const generateDisclaimer = () => {
    const { websiteName, websiteURL, country, state, email, collectUserData, lastUpdated } = formData;

    if (!websiteName || !websiteURL || !country || !state || !email) {
      toast.error("Please fill in all fields to generate the disclaimer.", {
        position: "top-center",
      });
      return;
    }

    const disclaimer = `
Disclaimer for ${websiteName}

Effective Date: ${lastUpdated}

The information provided on ${websiteName} ("we," "our," or "us"), accessible from ${websiteURL}, is for general informational purposes only. While we strive to keep the information up to date and correct, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability with respect to the website or the information, products, services, or related graphics contained on the website for any purpose. Any reliance you place on such information is strictly at your own risk.

**1. Professional Advice Disclaimer**
The content provided on ${websiteName} is not intended as professional advice of any kind, including but not limited to financial, legal, or medical advice. Always seek the advice of a qualified professional regarding any specific matter.

**2. External Links Disclaimer**
Our website may contain links to external websites that are not provided or maintained by us. We do not guarantee the accuracy, relevance, timeliness, or completeness of any information on these external websites.

**3. Liability Disclaimer**
In no event will we be liable for any loss or damage, including without limitation, indirect or consequential loss or damage, or any loss or damage whatsoever arising from loss of data or profits arising out of or in connection with the use of this website.

**4. Data Collection**
${collectUserData ? `
We collect user data to improve our services. The data collected includes:

- **Personal Information:** Names, email addresses, and contact details.
- **Usage Data:** Browsing activity, interactions, and preferences.` : `We do not collect any personal data from users.`}

**5. Contact Us**
If you have any questions or concerns about this disclaimer, please contact us at:

- **Email:** ${email}
- **Location:** ${state}, ${country}

By using ${websiteName}, you consent to this disclaimer and agree to its terms.
    `;

    const disclaimerHtml = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Disclaimer for ${websiteName}</title>
    </head>
    <body>
      <h1>Disclaimer for ${websiteName}</h1>
      <p>Effective Date: ${lastUpdated}</p>
      <p>The information provided on ${websiteName} ("we," "our," or "us"), accessible from ${websiteURL}, is for general informational purposes only. While we strive to keep the information up to date and correct, we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability with respect to the website or the information, products, services, or related graphics contained on the website for any purpose. Any reliance you place on such information is strictly at your own risk.</p>
    
      <h2>1. Professional Advice Disclaimer</h2>
      <p>The content provided on ${websiteName} is not intended as professional advice of any kind, including but not limited to financial, legal, or medical advice. Always seek the advice of a qualified professional regarding any specific matter.</p>

      <h2>2. External Links Disclaimer</h2>
      <p>Our website may contain links to external websites that are not provided or maintained by us. We do not guarantee the accuracy, relevance, timeliness, or completeness of any information on these external websites.</p>

      <h2>3. Liability Disclaimer</h2>
      <p>In no event will we be liable for any loss or damage, including without limitation, indirect or consequential loss or damage, or any loss or damage whatsoever arising from loss of data or profits arising out of or in connection with the use of this website.</p>
      
      <h2>4. Data Collection</h2>
      ${collectUserData ? `
      <ul>
        <li><strong>Personal Information:</strong> Names, email addresses, and contact details.</li>
        <li><strong>Usage Data:</strong> Browsing activity, interactions, and preferences.</li>
      </ul>` : `<p>We do not collect any personal data from users.</p>`}

      <h2>5. Contact Us</h2>
      <p>If you have any questions or concerns about this disclaimer, please contact us at:</p>
      <ul>
        <li><strong>Email:</strong> ${email}</li>
        <li><strong>Location:</strong> ${state}, ${country}</li>
      </ul>

      <p>By using ${websiteName}, you consent to this disclaimer and agree to its terms.</p>
    </body>
    </html>
    `;

    setGeneratedDisclaimer(disclaimer.trim());
    setDownloadDisclaimer(disclaimerHtml.trim());
    toast.success("Disclaimer generated successfully!", {
      position: "top-center",
    });
  };

  const downloadAsHtml = () => {
    if (!downloadDisclaimer) {
      toast.error("Nothing to download. Generate the disclaimer first!", {
        position: "top-center",
      });
      return;
    }

    const element = document.createElement("a");
    const file = new Blob([downloadDisclaimer], { type: "text/html" });
    element.href = URL.createObjectURL(file);
    element.download = `${formData.websiteName.replace(/\s+/g, "_")}_Disclaimer.html`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success("Disclaimer downloaded as HTML!", {
      position: "top-center",
    });
  };

  return (
    <div className="container mt-5">
      <ToastContainer />
     

      <div className="row justify-content-center">
        <div className="col-md-8 col-12">
          <div className="mb-3">
            <label className="form-label">Website Name:</label>
            <input
              type="text"
              name="websiteName"
              className="form-control"
              value={formData.websiteName}
              onChange={handleChange}
              placeholder="Enter your website name"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Website URL:</label>
            <input
              type="text"
              name="websiteURL"
              className="form-control"
              value={formData.websiteURL}
              onChange={handleChange}
              placeholder="Enter your website URL"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Country:</label>
            <input
              type="text"
              name="country"
              className="form-control"
              value={formData.country}
              onChange={handleChange}
              placeholder="Enter your country"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">State:</label>
            <input
              type="text"
              name="state"
              className="form-control"
              value={formData.state}
              onChange={handleChange}
              placeholder="Enter your state"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Email:</label>
            <input
              type="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
            />
          </div>

          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              name="collectUserData"
              id="collectUserData"
              checked={formData.collectUserData}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="collectUserData">
              Collect User Data
            </label>
          </div>

          <div className="mb-3">
            <label className="form-label">Last Updated Date:</label>
            <input
              type="date"
              name="lastUpdated"
              className="form-control"
              value={formData.lastUpdated}
              onChange={handleChange}
            />
          </div>

          <button className="btn btn-primary w-100 mb-3" onClick={generateDisclaimer}>
            Generate Disclaimer
          </button>
        </div>
      </div>

      {generatedDisclaimer && (
        <div className="row justify-content-center mt-4">
          <div className="col-md-8 col-12">
            <div className="border rounded p-4 bg-light">
              <h5 className="text-center">Generated Disclaimer</h5>
              <pre className="" style={{ whiteSpace: "pre-wrap" }}>
                {generatedDisclaimer}
              </pre>
              <div className="d-grid gap-2 mt-3">
                <button className="btn btn-secondary" onClick={downloadAsHtml}>
                  Download as HTML
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisclaimerGenerator;
